import { useState, useEffect } from 'react'
import api from '../api/axios'
import { ArrowPathIcon } from '@heroicons/react/24/outline'

interface QBConnection {
  qbCompany: string | null
  realmId: string | null
  qbStatus: string
  connectedBy: string | null
  connectedAt: string | null
  lastSyncAt: string | null
  tokenExpiresAt: string | null
  tokenHealthy: boolean
  refreshTokenExpiresAt: string | null
  daysUntilRefreshExpiry: number | null
  totalSynced: number
  recentFailures: number
  recentSyncs: number
  error?: string
}

interface TenantQB {
  companyId: string
  companyName: string
  companyCode: string
  status: string
  tier: string
  qbEnabled: boolean
  connection: QBConnection | null
}

interface StaleQB {
  companyId: string
  companyCode: string
  companyName: string
  companyStatus: string
  featureActive: boolean
  realmOpen: boolean
  connectionStatus: 'active' | 'needs_reconnect' | null
  reconnectRequiredAt: string | null
  lastError: string | null
  reason: string
  action: 'deactivate' | null
}

interface QBOverview {
  summary: {
    totalCompanies: number
    qbEnabled: number
    qbConnected: number
    tokenWarnings: number
    staleConnections?: number
  }
  tenants: TenantQB[]
  stale?: StaleQB[]
}

function staleReasonText(s: StaleQB): string {
  if (s.reason === 'feature_off') return 'QuickBooks feature is off, but still connected'
  if (s.reason.startsWith('company_inactive')) return `Company is ${s.companyStatus}, but QuickBooks is still connected`
  if (s.reason === 'reconnect_overdue') return 'Waiting more than 30 days for a reconnect'
  if (s.reason === 'needs_reconnect') return 'Needs reconnecting (QuickBooks rejected the saved sign-in)'
  if (s.reason === 'realm_without_connection') return 'Registered for webhooks, but no active connection'
  return s.reason
}

export default function QuickBooksManagement() {
  const [data, setData] = useState<QBOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<'all' | 'enabled' | 'connected' | 'warnings'>('all')
  const [clearing, setClearing] = useState<string | null>(null)
  const [clearMsg, setClearMsg] = useState('')

  useEffect(() => {
    loadOverview()
  }, [])

  const loadOverview = async () => {
    try {
      setLoading(true)
      setError('')
      const res = await api.get('/super-admin/quickbooks/overview')
      setData(res.data)
    } catch (err: any) {
      setError(err.response?.data?.error || err.message)
    } finally {
      setLoading(false)
    }
  }

  const clearCompany = async (s: StaleQB) => {
    if (!window.confirm(`Disconnect and clear QuickBooks for ${s.companyName}? Links and settings are kept so they can reconnect later.`)) return
    try {
      setClearing(s.companyId)
      setClearMsg('')
      const res = await api.post(`/super-admin/quickbooks/${encodeURIComponent(s.companyId)}/clear`)
      const r = res.data || {}
      setClearMsg(`${s.companyName}: ${r.disconnected ?? 0} connection(s) disconnected, ${r.realmsClosed ?? 0} realm(s) closed, ${r.batchesCancelled ?? 0} batch(es) cancelled${r.errors?.length ? ` (with ${r.errors.length} error(s))` : ''}.`)
      await loadOverview()
    } catch (err: any) {
      setClearMsg(err.response?.data?.error || err.message)
    } finally {
      setClearing(null)
    }
  }

  const filteredTenants = data?.tenants.filter(t => {
    switch (filter) {
      case 'enabled': return t.qbEnabled
      case 'connected': return t.connection && !t.connection.error && t.connection.qbStatus === 'active'
      case 'warnings': return t.connection?.daysUntilRefreshExpiry != null && t.connection.daysUntilRefreshExpiry <= 14
      default: return true
    }
  }) || []

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" />
          <p className="text-gray-500 text-sm">Loading QuickBooks overview...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-700 font-medium">{error}</p>
          <button onClick={loadOverview} className="mt-3 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">
            Retry
          </button>
        </div>
      </div>
    )
  }

  const s = data!.summary

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 40 40" className="w-9 h-9" aria-hidden>
              <circle cx="20" cy="20" r="20" fill="#2CA01C" />
              <text x="20" y="26" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold" fontFamily="sans-serif">Q</text>
            </svg>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">QuickBooks Management</h1>
              <p className="text-sm text-slate-500 mt-0.5">Monitor and manage QuickBooks connections across all tenants</p>
            </div>
          </div>
          <button onClick={loadOverview} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <button onClick={() => setFilter('all')}
          className={`rounded-xl border p-4 text-left transition ${filter === 'all' ? 'ring-2 ring-indigo-500 border-indigo-300' : 'border-gray-200 hover:border-gray-300'}`}>
          <p className="text-3xl font-bold text-gray-900">{s.totalCompanies}</p>
          <p className="text-xs text-gray-500 mt-1">Total Companies</p>
        </button>
        <button onClick={() => setFilter('enabled')}
          className={`rounded-xl border p-4 text-left transition ${filter === 'enabled' ? 'ring-2 ring-green-500 border-green-300' : 'border-gray-200 hover:border-gray-300'}`}>
          <p className="text-3xl font-bold text-green-700">{s.qbEnabled}</p>
          <p className="text-xs text-gray-500 mt-1">QB Enabled</p>
        </button>
        <button onClick={() => setFilter('connected')}
          className={`rounded-xl border p-4 text-left transition ${filter === 'connected' ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200 hover:border-gray-300'}`}>
          <p className="text-3xl font-bold text-blue-700">{s.qbConnected}</p>
          <p className="text-xs text-gray-500 mt-1">Connected</p>
        </button>
        <button onClick={() => setFilter('warnings')}
          className={`rounded-xl border p-4 text-left transition ${filter === 'warnings' ? 'ring-2 ring-amber-500 border-amber-300' : 'border-gray-200 hover:border-gray-300'}`}>
          <p className={`text-3xl font-bold ${s.tokenWarnings > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{s.tokenWarnings}</p>
          <p className="text-xs text-gray-500 mt-1">Token Warnings</p>
        </button>
      </div>

      {/* Stale connections */}
      {(data?.stale?.length ?? 0) > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-amber-100 bg-amber-50">
            <h2 className="font-semibold text-amber-900">
              Stale connections
              <span className="ml-2 text-sm font-normal text-amber-700">({data!.stale!.length})</span>
            </h2>
            <p className="text-xs text-amber-800 mt-0.5">
              Cleared automatically by the daily sweep when marked "auto". Clear disconnects QuickBooks, closes the webhook registration and cancels pending batches; links and settings are kept.
            </p>
          </div>
          {clearMsg && <p className="px-6 py-2 text-sm text-gray-700 border-b border-gray-100">{clearMsg}</p>}
          <div className="divide-y divide-gray-100">
            {data!.stale!.map(st => (
              <div key={st.companyId} className="px-6 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 truncate">{st.companyName}</p>
                    <span className="text-xs text-gray-400 font-mono">{st.companyCode}</span>
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded">{st.companyStatus}</span>
                    {st.action === 'deactivate' && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 rounded">auto</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">{staleReasonText(st)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Connection: {st.connectionStatus ?? 'none'} · Webhook realm: {st.realmOpen ? 'open' : 'closed'} · Feature: {st.featureActive ? 'on' : 'off'}
                    {st.reconnectRequiredAt && <> · Since {new Date(st.reconnectRequiredAt).toLocaleDateString()}</>}
                    {st.lastError && <> · {st.lastError}</>}
                  </p>
                </div>
                <button
                  onClick={() => clearCompany(st)}
                  disabled={clearing === st.companyId}
                  className="px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                >
                  {clearing === st.companyId ? 'Clearing...' : 'Clear'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tenant List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">
            {filter === 'all' ? 'All Companies' : filter === 'enabled' ? 'QB-Enabled Companies' : filter === 'connected' ? 'Connected Companies' : 'Token Warnings'}
            <span className="ml-2 text-sm font-normal text-gray-400">({filteredTenants.length})</span>
          </h2>
        </div>

        {filteredTenants.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-400">
            <p className="text-sm">No companies match this filter</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredTenants.map(t => (
              <div key={t.companyId} className="px-6 py-4 flex items-center gap-4">
                {/* Status indicator */}
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                  !t.qbEnabled ? 'bg-gray-300'
                  : t.connection?.qbStatus === 'active' ? 'bg-green-500'
                  : t.connection?.qbStatus === 'expired' || t.connection?.qbStatus === 'needs_reconnect' ? 'bg-amber-500'
                  : t.connection?.error ? 'bg-red-500'
                  : 'bg-gray-300'
                }`} />

                {/* Company info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 truncate">{t.companyName}</p>
                    <span className="text-xs text-gray-400 font-mono">{t.companyCode}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded ${
                      t.tier === 'enterprise' ? 'bg-purple-100 text-purple-700'
                      : t.tier === 'premium' ? 'bg-blue-100 text-blue-700'
                      : t.tier === 'standard' ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                    }`}>
                      {t.tier}
                    </span>
                    {!t.qbEnabled && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded">QB disabled</span>
                    )}
                  </div>

                  {t.connection && !t.connection.error && (
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      <span>QB: <strong className="text-gray-700">{t.connection.qbCompany || '—'}</strong></span>
                      <span>Realm: {t.connection.realmId}</span>
                      <span>By: {t.connection.connectedBy || '—'}</span>
                      {t.connection.lastSyncAt && (
                        <span>Last sync: {new Date(t.connection.lastSyncAt).toLocaleDateString()}</span>
                      )}
                    </div>
                  )}

                  {t.connection?.error && (
                    <p className="text-xs text-red-500 mt-0.5">DB Error: {t.connection.error}</p>
                  )}
                </div>

                {/* Stats */}
                {t.connection && !t.connection.error && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-center">
                      <p className="text-sm font-bold text-green-700">{t.connection.totalSynced}</p>
                      <p className="text-[10px] text-gray-400">synced</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-blue-700">{t.connection.recentSyncs}</p>
                      <p className="text-[10px] text-gray-400">7d syncs</p>
                    </div>
                    {t.connection.recentFailures > 0 && (
                      <div className="text-center">
                        <p className="text-sm font-bold text-red-600">{t.connection.recentFailures}</p>
                        <p className="text-[10px] text-gray-400">7d fails</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Token health badge */}
                {t.connection && !t.connection.error && (
                  <div className="flex-shrink-0">
                    {t.connection.daysUntilRefreshExpiry != null && t.connection.daysUntilRefreshExpiry <= 14 ? (
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                        t.connection.daysUntilRefreshExpiry <= 7
                          ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {t.connection.daysUntilRefreshExpiry}d left
                      </span>
                    ) : t.connection.qbStatus === 'active' ? (
                      <span className="px-2.5 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                        Healthy
                      </span>
                    ) : t.connection.qbStatus === 'expired' || t.connection.qbStatus === 'needs_reconnect' ? (
                      <span className="px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                        Needs reconnect
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
                        {t.connection.qbStatus}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  )
}
